import { Requirement, RequirementItem } from "@/types/requirement";
import { App, Modal, ModalFuncProps, Spin, InputNumber, notification, Form, Input, Switch, Select, Button, Flex } from "antd";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { orderAdd, orderDel, orderPreOut, realList } from "@/apis/inventoryv2";
import { useTranslation } from "react-i18next";
import { InventoryV2, OrderAddItemV2, OrderAddV2, OrderV2 } from "@/types/inventoryV2";
import type { ColumnsType } from "antd/es/table";
import { ExclamationCircleFilled } from "@ant-design/icons";
import SText from "@/styles/SText";
import { SourceTypeEnum } from "@/types/common";
import { ChangeEnum, OperateEnum } from "@/types/inventory";
import type { OutQtyObj } from "./PreOutOrderPrint";
import { numFormmater } from "@/pages/Requirement/util";
import {
  recalculatePickOutRowsByGenerateCount,
  clampGenerateCount,
  clampPickOutDeductCount,
  getFinalPickOutNum,
  getGenerateCountLimit,
  getSubmitPickOutNum,
} from "./RequirementPickOutUtils";
import SelectLocationType from "@/components/Selector/SelectLocationType";
import CommonTable from "@/components/CommonTable";
import errorCodes from "@/apis/error";

interface PickOutData extends RequirementItem {
  stockNum: number;        //库存数量
  stockUseNum: number;     //可用库存数量
  preIn: number;           //预入库数量
  preOut: number;          //预出库数量
  pickOutNum: number;      //出库数量
  deductOutCount?: number | null; //扣减已出库数量
  maxOutNum: number;       //最大出库数量
  defaultPickOutNum: number;//默认出库数量
}

interface RequirementPickOutProps extends ModalFuncProps {
  selectedRows: Array<RequirementItem>;
  preOutQtyMap: Record<string, OutQtyObj>;
  requirement?: Requirement | null;
  onClose: () => void;
  onSuccess: () => void;
}
const RequirementPickOut: FC<RequirementPickOutProps> = ({ selectedRows, preOutQtyMap, requirement, onClose, onSuccess, ...restProps }) => {

  const { t } = useTranslation();
  const { modal } = App.useApp();
  const [datas, setdatas] = useState<Array<PickOutData>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [desc, setDesc] = useState<string>('');
  const [preOutLocationTypes, setPreOutLocationTypes] = useState<number[]>([1]);
  const [needReMatch, setNeedReMatch] = useState<boolean>(false);
  const generateCountLimit = getGenerateCountLimit(requirement?.requireCount);
  const [generateCount, setGenerateCount] = useState<number | null>(null);
  const [deductEnabled, setDeductEnabled] = useState<boolean>(false);
  const canShowGenerateCount = selectedRows.length > 0;
  const activeGenerateCount = canShowGenerateCount ? generateCount : null;
  const hasGenerateCount = activeGenerateCount !== null && activeGenerateCount >= 1;
  const generateCountRef = useRef<number | null>(activeGenerateCount);

  useEffect(() => {
    generateCountRef.current = activeGenerateCount;
  }, [activeGenerateCount]);

  useEffect(() => {
    // 生产数量变化时，按需求物料行的 bomAmount 和损耗率计算。
    setdatas(last => recalculatePickOutRowsByGenerateCount(last, activeGenerateCount));
  }, [activeGenerateCount]);

  const matchStockData = useCallback(() => {
    if (selectedRows.length === 0) return;

    const newValues = [...selectedRows];

    setLoading(true);
    let resMatch: PickOutData[] = [];
    let stockObj: { [key: string]: InventoryV2 } = {};

    realList({ body: { ids: newValues.map(data => data.productId), locationTypes: preOutLocationTypes } })
      .then(res => {
        if (Array.isArray(res?.body) && res.body.length > 0) {
          res.body.forEach(stock => {
            stockObj[stock.productId] = { ...stock };
          });
        }
        newValues.forEach((nv, index) => {
          const stock = stockObj[nv.productId];

          const useAmount = nv?.useAmount ?? 0; //  需求数量
          const outCount = nv?.outCount ?? 0; //  出库数量
          const preOutQty = preOutQtyMap[nv.id]?.preOutQty ?? 0; // 当前需求行预出库数量

          const stockCount = stock?.count ?? 0;
          const stockPreOut = stock?.preOut ?? 0;

          let stockUseNum = Math.max(stockCount - stockPreOut, 0); //  可用库存数量
          let preOutNum = Math.max(Number(useAmount || 0) - Number(outCount || 0) - preOutQty, 0); //  可预出库数量

          const maxOutNum = Math.min(preOutNum, stockUseNum) //  最大预出库数量

          let matched: PickOutData = {
            ...newValues[index],
            stockNum: stockCount,
            stockUseNum,
            preIn: stock?.preIn ?? 0,
            preOut: preOutQty,
            pickOutNum: maxOutNum,
            deductOutCount: null,
            maxOutNum: maxOutNum,       //可能会更改
            // 保留旧逻辑的默认值，清空生产数量时需要恢复到这个数量。
            defaultPickOutNum: maxOutNum,
          };
          resMatch.push({ ...matched });
        });
        setNeedReMatch(false);
        setdatas(recalculatePickOutRowsByGenerateCount(resMatch, generateCountRef.current));
      })
      .catch(err => notification.error({ placement: 'top', message: t("bom.matchErr"), description: err?.message || "" }))
      .finally(() => {
        setLoading(false);
      });
  }, [selectedRows, preOutQtyMap, t, preOutLocationTypes]);

  useEffect(() => {
    selectedRows.length > 0 && matchStockData();
  }, [selectedRows.length]);

  useEffect(() => {
    if (!hasGenerateCount) {
      // 生产数量隐藏或清空时，同步关闭全局扣减，避免隐藏输入框里的临时扣减值继续影响提交。
      setDeductEnabled(false);
      setdatas(last => last.map(row => ({ ...row, deductOutCount: null })));
    }
  }, [hasGenerateCount]);

  useEffect(() => {
    if (!canShowGenerateCount && generateCount !== null) {
      // 没有物料行时清空生产数量。
      setGenerateCount(null);
    }
  }, [canShowGenerateCount, generateCount]);

  const handleDeductEnabledChange = (checked: boolean) => {
    setDeductEnabled(checked);
    setdatas(last => last.map(row => ({
      ...row,
      // 开启时默认按每行已出库数量扣减，但不能超过当前本次预出库数量；关闭时清空临时扣减值。
      deductOutCount: checked ? clampPickOutDeductCount(row.outCount, row.pickOutNum) : null,
    })));
  };

  const columns: ColumnsType<PickOutData> = [
    {
      title: t("product.code"),
      dataIndex: "productId",
      key: "productId",
      width: 80,
      fixed: "left",
      render: (_: any, record: PickOutData) => <SText text={record.product?.code ?? ''} />
    },
    {
      title: t("product.name"),
      dataIndex: "name",
      key: "name",
      width: 80,
      render: (_: any, record: PickOutData) => record.product?.name || "-",
    },
    {
      title: t("product.model"),
      dataIndex: "model",
      key: "model",
      width: 120,
      render: (_: any, record: PickOutData) => <SText text={record.product?.model || "-"} />,
    },
    {
      title: <SText text={t("product.uom")} />,
      dataIndex: "uom",
      key: "uom",
      width: 70,
      render: (_: any, record: PickOutData) => record.product?.uom || "-",
    },
    {
      title: <SText text={t("requirement.bomAmount")} />,
      dataIndex: "bomAmount",
      key: "bomAmount",
      width: 70,
      render: (_: any, record: PickOutData) => <SText text={numFormmater(Number(record.bomAmount || "0"))} />
    },
    {
      title: t("requirement.requireCount"),
      dataIndex: "useAmount",
      key: "useAmount",
      width: 88,
      render: (_: any, record: PickOutData) => <SText text={numFormmater(Number(record.useAmount || "0"))} />
    },
    // {
    //   title: t("bom.stockNum"),
    //   dataIndex: "stockNum",
    //   key: "stockNum",
    //   width: 90,
    //   render: (text: any) => text ?? 0,
    // },
    // {
    //   title: t("bom.preStock"),
    //   dataIndex: "preOut",
    //   key: "preOut",
    //   width: 90,
    //   render: (text: any) => text ?? 0,
    // },
    // {
    //   title: t("bom.preInStock"),
    //   dataIndex: "preIn",
    //   key: "preIn",
    //   width: 90,
    //   render: (text: any) => text ?? 0,
    // },
    {
      title: t("bom.useStock"),
      dataIndex: "stockUseNum",
      key: "stockUseNum",
      width: 100,
      render: (_, record) => <SText text={numFormmater(record.stockUseNum)} />,
    },
    {
      title: t("purchase.purCount"),
      dataIndex: "purCount",
      key: "purCount",
      width: 88,
      render: (_, record) => <SText text={numFormmater(Number(record.purCount || ''))} />,
    },
    {
      title: t("requirement.outCount"),
      dataIndex: "outCount",
      key: "outCount",
      width: 88,
      render: (text: any) => <SText text={numFormmater(Number(text ?? 0))} />,
    },
    {
      title: t("requirement.alrPreOutNum"),
      dataIndex: "preOut",
      key: "preOut",
      width: 100,
      render: (text: any) => <SText text={numFormmater(Number(text ?? 0))} />,
    },
    {
      title: t("requirement.prePickOutNum"),
      dataIndex: "pickOutNum",
      key: `pickOutNum-${deductEnabled}`,
      fixed: "right",
      width: deductEnabled ? 312 : 120,
      render: (text: any, record: PickOutData, index: number) => {
        // 展示保留本次预出库数量；提交时再按实际可出库数量取上限。
        const finalPickOutNum = getFinalPickOutNum(record, deductEnabled);

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
            <InputNumber changeOnWheel
              formatter={val => numFormmater(Number(val || ''))}
              min={0}
              // 生产数量模式允许展示 BOM 计算值，提交时会自动按实际可出库数量取较小值。
              max={hasGenerateCount ? undefined : record.maxOutNum}
              value={text}
              style={{ width: 108, flex: "0 0 108px" }}
              onChange={(val) => {
                const inputNum = Math.max(Number(val || 0), 0);
                const pickOutNum = hasGenerateCount ? inputNum : Math.min(inputNum, record.maxOutNum);
                const deductOutCount = clampPickOutDeductCount(record.deductOutCount, pickOutNum);
                const newDatas = [...datas];
                newDatas[index] = { ...record, pickOutNum, deductOutCount };
                setdatas(newDatas);
              }}
            />
            {deductEnabled && (
              <>
                <span>-</span>
                <InputNumber changeOnWheel
                  formatter={val => numFormmater(Number(val || ''))}
                  min={0}
                  max={record.pickOutNum}
                  value={record.deductOutCount}
                  placeholder={t("requirement.deductOutCount")}
                  style={{ width: 108, flex: "0 0 108px" }}
                  onChange={(val) => {
                    const newDatas = [...datas];
                    newDatas[index] = {
                      ...record,
                      // 扣减值是本弹窗临时值，只用于算最终提交 count。
                      deductOutCount: clampPickOutDeductCount(val, record.pickOutNum),
                    };
                    setdatas(newDatas);
                  }}
                />
                <span>=</span>
                <span style={{ minWidth: 40 }}>
                  {numFormmater(finalPickOutNum)}
                </span>
              </>
            )}
          </div>
        );
      },
    },
    {
      title: t("requirement.actualOutNum"),
      dataIndex: "maxOutNum",
      key: "maxOutNum",
      fixed: "right",
      width: 96,
      render: (_, record) => <SText text={numFormmater(Number(record.maxOutNum || 0))} />,
    },
  ];

  const handleDelTip = (delOrder: OrderV2) => {
    modal.confirm({
      title: t('common.tip'),
      content: t('requirement.pickErr', { number: delOrder.number }),
      cancelText: t('warehouse.notDelClose'),
      okText: t('warehouse.delOrderClose'),
      okButtonProps: { danger: true },
      async onOk() {
        await orderDel(delOrder.id);
        notification.info({ message: t('requirement.pickErrDel') })
        onClose();
      },
      onCancel() {
        notification.info({ message: t('requirement.pickErrTip', { number: delOrder.number }) });
        onSuccess();
      }
    })
  };

  const submitAddOut = async () => {
    if (loading) return
    if (!preOutLocationTypes.length) {
      return notification.warning({
        placement: 'top',
        message: '请选择出库匹配库位类型',
      });
    }
    setLoading(true);
    // 构造 OrderAddV2；字段结构不变，count 按实际可出库数量取上限。
    const items: OrderAddItemV2[] = datas
      // .filter(row => (row.pickOutNum || 0) > 0)
      .map(row => {
        let item = selectedRows.find(r => r.productId === row.productId);
        return {
          productId: row.productId,
          count: getSubmitPickOutNum(row, deductEnabled),
          lot: "",
          thdlot: "",
          desc: item?.tags?.map(t => t.tag)?.join(",") ?? '',
          locations: [],
          sourceId: row.id,
          sourceType: SourceTypeEnum.REQUIREMENT_ITEM
        };
      });
    let order: OrderV2 | null = null;
    if (items.length === 0) {
      setLoading(false)
      return notification.error({
        message: t('requirement.atLeastOnePreOutGreaterThanZero')
      })
    }
    const orderData: OrderAddV2 = {
      type: ChangeEnum.PRE_OUT,
      opType: OperateEnum.PICKING,
      desc,
      preTime: null,
      arrivalAt: null,
      items
    };
    try {
      // 1. 创建出库订单
      order = await orderAdd(orderData);
      await orderPreOut(order.id, preOutLocationTypes)
      notification.success({
        placement: "top",
        message: t("requirement.createPreOutInventorySuccess"),
        description: order.number
      });
      setLoading(false);
      onSuccess();
    } catch (addError: any) {
      setLoading(false);
      if (addError?.code === 73010) {
        notification.error({
          message: t('inventory.proQtyFalse'),
          description: addError?.extra?.map((i: { productId: string }) => i.productId)?.join(', ')
        })
      } else if ([73022, 73014].includes(addError)) {
        notification.error({
          message: t('inventory.calcErr')
        })
      } else if ([73024, 73028].includes(addError?.code)) {
        notification.error({
          message: t('warehouse.insufficientInventory'),
          description: addError?.extra?.productCode
        })
      };
      if (order) handleDelTip(order)
      else notification.error({ message: errorCodes[addError] || t("requirement.pickErrDel") });
    }
  }

  const handleAddOut = () => {
    modal.confirm({
      title: <span>{t("common.tip")}</span>,
      icon: <ExclamationCircleFilled />,
      content: t("requirement.confirmPickOut"),
      cancelText: t("common.cancel"),
      okText: t("common.confirm"),
      onOk: () => submitAddOut(),
    });
  };

  return (
    <Modal open
      title={t("requirement.prePickOut")}
      onCancel={onClose}
      onOk={() => handleAddOut()}
      okButtonProps={{ disabled: loading || needReMatch }}
      width={1000}
      centered
      maskClosable={false}
      {...restProps}
    >
      <Spin spinning={loading} fullscreen={false}>
        <Flex gap={4} align='baseline' >
          <Form.Item required label={t("requirement.matchLocType")} style={{ width: "100%" }}>
            <SelectLocationType
              mode="multiple"
              style={{ flex: 1 }}
              value={preOutLocationTypes}
              onChange={val => {
                setPreOutLocationTypes(val);
                !needReMatch && notification.info({ message: t('requirement.rematchTip') })
                setNeedReMatch(true);
              }}
              maxTagCount={1}
            />
          </Form.Item>
          <Button disabled={!needReMatch || loading} onClick={() => matchStockData()} >{t('requirement.matchInventory')}</Button>
        </Flex>
        <Form.Item label={t("requirement.preOutDesc")}>
          <Input.TextArea value={desc} onChange={e => setDesc(e.target.value)} />
        </Form.Item>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 16 }}>
          {canShowGenerateCount && (
            <Form.Item label={t("requirement.prNum")} style={{ marginBottom: 16 }}>
              <InputNumber changeOnWheel
                min={1}
                max={generateCountLimit}
                value={generateCount}
                style={{ width: 120 }}
                onChange={(val) => {
                  // 生产数量改变后，整表的本次预出库数量都会重算。
                  setGenerateCount(clampGenerateCount(val, generateCountLimit));
                }}
              />
            </Form.Item>
          )}
          {hasGenerateCount && (
            <Form.Item label={t("requirement.deductOutCount")} style={{ marginBottom: 16 }}>
              <Switch
                size="small"
                checked={deductEnabled}
                onChange={handleDeductEnabledChange}
              />
            </Form.Item>
          )}
        </div>
        <CommonTable
          columns={columns}
          dataSource={datas}
          rowKey="id"
          pagination={false}
          virtual
          bordered={false}
          scroll={{ x: 1200, y: 400 }}
          size="small"
        />
        {/* <span style={{ color: "red" }}>{t('requirement.overInventoryTip')}</span> */}
      </Spin>
    </Modal>
  )
};

export default RequirementPickOut;
